import React, { useEffect, useMemo, useState } from 'react';
import Header from './Header';
import { apiService } from '../services/api';

interface ClinicalPublicationRow {
  pub_date?: string;
  title?: string;
  journal?: string;
  pmid?: string;
  indication?: string;
  indication_name?: string;
  disease?: string;
  tumor_type?: string;
  cancer_type?: string;
  indication_details?: string;
  subindication?: string;
}

const ClinicalPublicationsView: React.FC = () => {
  const [rows, setRows] = useState<ClinicalPublicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadPublications = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchClinicalPublications();
        if (response?.success) {
          setRows(response.data || []);
        } else {
          setError(response?.error || 'Failed to load publications');
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load publications');
      } finally {
        setLoading(false);
      }
    };
    loadPublications();
  }, []);

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filteredRows = useMemo(() => {
    let list = rows;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = rows.filter((row) => {
        const values = [row.title, row.journal, row.pmid, row.pub_date];
        return values.some((value) =>
          value != null && String(value).toLowerCase().includes(query)
        );
      });
    }
    // Sort by publication date (newest first), then by PMID (desc) as tiebreaker
    const parseDate = (v?: string): number => {
      if (!v) return 0;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const pmidNum = (v?: string): number => {
      if (v == null || v === '') return 0;
      const n = parseInt(String(v), 10);
      return Number.isNaN(n) ? 0 : n;
    };
    return [...list].sort((a, b) => {
      const da = parseDate(a.pub_date);
      const db = parseDate(b.pub_date);
      if (db !== da) return db - da;
      return pmidNum(b.pmid) - pmidNum(a.pmid);
    });
  }, [rows, searchQuery]);

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />

      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
          <h1 className="text-3xl font-bold text-slate-900">Clinical Journal Publications</h1>
          <p className="text-slate-500 text-sm mt-2">Latest oncology journal publications.</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-12">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-center text-rose-600 font-medium">{error}</p>
          </div>
        ) : (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-white flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Publications Table</h2>
                <p className="text-sm text-slate-500 mt-1">{filteredRows.length} publications</p>
              </div>
              <div className="w-full md:w-auto flex flex-col md:flex-row gap-3 md:items-center">
                <div className="w-full md:w-72">
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search publications..."
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '52%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead className="bg-slate-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Publication Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Journal</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">PMID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row, idx) => (
                    <tr key={`${row.pmid || 'pub'}-${idx}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap align-top">
                        {formatDate(row.pub_date)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 whitespace-normal break-words align-top" title={row.title || ''}>
                        {row.title || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-normal break-words align-top" title={row.journal || ''}>
                        {row.journal || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap align-top" title={row.pmid || ''}>
                        {row.pmid ? (
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${row.pmid}/`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            {row.pmid}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClinicalPublicationsView;
